/**
 * Toast Component
 *
 * A simple, non-intrusive toast notification for status messages.
 * Styled to match Fairdrop's design system.
 */

import { useEffect } from 'react'

export interface ToastProps {
  /** Whether the toast is visible */
  visible: boolean
  /** Main message */
  message: string
  /** Optional description below the message */
  description?: string
  /** Toast type for styling */
  type?: 'info' | 'success' | 'warning' | 'error'
  /** Auto-dismiss duration in ms (0 = no auto-dismiss) */
  duration?: number
  /** Callback when toast is dismissed */
  onDismiss?: () => void
}

export function Toast({
  visible,
  message,
  description,
  type = 'info',
  duration = 5000,
  onDismiss,
}: ToastProps) {
  useEffect(() => {
    if (visible && duration > 0 && onDismiss) {
      const timer = setTimeout(onDismiss, duration)
      return () => clearTimeout(timer)
    }
  }, [visible, duration, onDismiss])

  if (!visible) return null

  return (
    <div className={`toast toast-${type}`} role="alert">
      <div className="toast-content">
        <span className="toast-message">{message}</span>
        {description && <span className="toast-description">{description}</span>}
      </div>
      {onDismiss && (
        <button className="toast-close" onClick={onDismiss} aria-label="Dismiss">
          &times;
        </button>
      )}
    </div>
  )
}

export default Toast
